import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonToast,
  RefresherEventDetail,
} from '@ionic/react';
import {
  bookmarkOutline,
  bookmark,
  starOutline,
  star,
  chevronForwardOutline,
} from 'ionicons/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import ProPositionSheet from '../../components/pro/bottom-sheet/ProPositionSheet';
import ProTrackingWorkspacePicker from '../../components/pro/bottom-sheet/ProTrackingWorkspacePicker';
import ProTrackingDestinationLabel from '../../components/pro/ProTrackingDestinationLabel';
import { getPositionColorClasses } from '../../utils/positionColors';
import { formatPartyDisplay, getPartyBadgeColor } from '../../utils/partyUtils';
import { formatDistrict } from '../../utils/districtDisplay';
import { useProTrackingWorkspace } from './useProTrackingWorkspace';
import { trackingCapabilitiesFromAccessLevel } from './trackingCapabilitiesFromAccessLevel';
import {
  buildLegislatorTrackBody,
  buildLegislatorUntrackBody,
  findTrackedLegislator,
} from './findTrackedLegislator';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Legislator {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  party_affiliation: string;
  chamber: string;
  district: string;
  session_year?: string;
  email?: string;
  work_phone?: string;
  organized_contacts?: {
    emails: Array<{ value: string; context?: string; is_primary?: boolean }>;
    phones: Array<{ value: string; context?: string; is_primary?: boolean }>;
    websites: Array<{ value: string; context?: string }>;
    social_media: Array<{ value: string; platform?: string; handle?: string }>;
    other: any[];
  };
  organized_addresses?: {
    capitol: Array<{ address_line_1?: string; city?: string; state?: string; zip_code?: string }>;
    office: Array<{ address_line_1?: string; city?: string; state?: string; zip_code?: string }>;
    district: Array<{ address_line_1?: string; city?: string; state?: string; zip_code?: string }>;
    [key: string]: any;
  };
  contact_info?: { website_url?: string | null; personal_website_url?: string | null };
  committee_memberships?: Array<{ committee_name: string; chamber: string; role?: string }>;
  committees?: string; // comma-separated string from main query
  person_info?: any;
}

interface LegislatorStats {
  sponsored_bills_count?: number;
  sponsoredBillsCount?: number;
  voting_participation?: number;
  session_year?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getInitials = (leg: Legislator): string => {
  const first = leg.first_name?.[0] || '';
  const last = leg.last_name?.[0] || '';
  return (first + last).toUpperCase() || '?';
};

const getPartyBgColor = (party: string): string => {
  const p = (party || '').toLowerCase();
  if (p.includes('democrat')) return 'bg-party-democrat';
  if (p.includes('republican')) return 'bg-party-republican';
  return 'bg-party-unknown dark:bg-party-unknown-bg-dark';
};

const getTitle = (leg: Legislator): string => {
  const c = (leg.chamber || '').toLowerCase();
  if (c.includes('senate')) return 'Sen.';
  if (c.includes('house')) return 'Rep.';
  return '';
};

const getRoleBadgeColor = (role?: string): string => {
  const r = (role || '').toLowerCase();
  if (r === 'chair') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (r === 'vice chair' || r === 'vice-chair') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-background-tertiary text-neutral-600 dark:bg-background-dark-secondary/30 dark:text-text-dark-tertiary';
};


// ---------------------------------------------------------------------------
// Tracking pending action (needs-choice → picker → resume write)
// ---------------------------------------------------------------------------

type PendingTrackingAction =
  | { kind: 'track' }
  | { kind: 'untrack' }
  | { kind: 'position'; payload: string | null }
  | { kind: 'priority'; payload: boolean }
  | { kind: 'open-position' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProLegislatorDetailPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // ── UI State ───────────────────────────────────────────────────────────────
  const [showToast, setShowToast] = useState('');
  const [positionSelectorOpen, setPositionSelectorOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingTrackingAction | null>(null);

  // Router state key shared with ProWorkspaceDetailPage (Task 5): trackingWorkspaceId
  const contextId =
    (location.state as { trackingWorkspaceId?: string } | null)?.trackingWorkspaceId ??
    null;

  const tracking = useProTrackingWorkspace(contextId);
  const { status, eligible, select } = tracking;
  const selectedWorkspace =
    tracking.status === 'selected' ? tracking.workspace : null;
  const selectedId = selectedWorkspace?.id ?? null;
  const trackingControlsDisabled =
    status === 'none' || status === 'context-ineligible';
  const { canWrite, canManage } = trackingCapabilitiesFromAccessLevel(
    selectedWorkspace?.access_level,
  );
  const writeControlsDisabled =
    trackingControlsDisabled || (status === 'selected' && !canWrite);
  const positionControlsDisabled =
    trackingControlsDisabled || (status === 'selected' && !canManage);
  const positionUnavailableExplanation =
    status === 'selected' && !canManage
      ? t('legislatorDetail.positionRequiresManage')
      : null;

  // ── Legislator Query ───────────────────────────────────────────────────────
  const {
    data: legislator,
    isLoading,
    refetch: refetchLegislator,
  } = useQuery<Legislator>(
    ['pro-legislator', id],
    async () => {
      const response = await api.get(`/legislators/${id}`);
      return response.data?.data || response.data;
    },
    { staleTime: 60000 }
  );

  // ── Stats Query ────────────────────────────────────────────────────────────
  const {
    data: statsRaw,
    refetch: refetchStats,
  } = useQuery(
    ['pro-legislator-stats', id],
    async () => {
      const response = await api.get(`/legislators/${id}/stats`);
      return response.data?.data || response.data;
    },
    { staleTime: 60000 }
  );

  const stats: LegislatorStats | null = statsRaw || null;

  // ── Tracking Status Query ──────────────────────────────────────────────────
  const { data: trackingStatus } = useQuery(
    ['pro-legislator-tracking', id, selectedId],
    async () => {
      if (!selectedId) return null;
      const { data } = await api.get(`/workspace-legislator-tracking/workspace/${selectedId}`);
      const items = data.data || data || [];
      return findTrackedLegislator(items, id);
    },
    { enabled: !!selectedId }
  );

  const isTracked = !!trackingStatus;
  const isPriority = !!(trackingStatus?.is_priority || trackingStatus?.isPriority);
  const currentPosition: string | null =
    typeof trackingStatus?.position === 'string' ? trackingStatus.position : null;

  // ── Cache invalidation (detail + list / workspace lists — AC6) ─────────────
  const invalidateLegislatorTrackingCaches = (wsId: string | null | undefined) => {
    if (!wsId) return;
    queryClient.invalidateQueries(['pro-legislator-tracking', id, wsId]);
    queryClient.invalidateQueries(['pro-tracked-bills', wsId]);
    queryClient.invalidateQueries(['pro-tracked-legislators', wsId]);
    queryClient.invalidateQueries(['pro-workspace-bills', wsId]);
    queryClient.invalidateQueries(['pro-workspace-legislators', wsId]);
  };

  // ── Track Mutation ─────────────────────────────────────────────────────────
  const trackMutation = useMutation(
    ({ action, wsId }: { action: 'track' | 'untrack'; wsId: string }) => {
      if (!id) {
        return Promise.reject(new Error('No tracking workspace selected'));
      }
      return action === 'track'
        ? api.post('/workspace-legislator-tracking', buildLegislatorTrackBody(id, wsId))
        : api.delete('/workspace-legislator-tracking', {
            data: buildLegislatorUntrackBody(id, wsId),
          });
    },
    {
      onSuccess: (_data, { action, wsId }) => {
        invalidateLegislatorTrackingCaches(wsId);
        setShowToast(action === 'track' ? t('legislatorDetail.trackAdded') : t('legislatorDetail.trackRemoved'));
      },
      onError: () => {
        setShowToast(t('legislatorDetail.trackError'));
      },
    }
  );

  // ── Priority Mutation ──────────────────────────────────────────────────────
  const priorityMutation = useMutation(
    ({ newIsPriority, wsId }: { newIsPriority: boolean; wsId: string }) =>
      api.put(
        `/workspace-legislator-tracking/workspace/${wsId}/legislator/${id}/priority`,
        { isPriority: newIsPriority }
      ),
    {
      onSuccess: (_data, { wsId }) => {
        invalidateLegislatorTrackingCaches(wsId);
        setShowToast(t('legislatorDetail.priorityUpdated'));
      },
      onError: () => {
        setShowToast(t('legislatorDetail.priorityError'));
      },
    }
  );

  // ── Position Mutation ──────────────────────────────────────────────────────
  const positionMutation = useMutation(
    ({ position, wsId }: { position: string | null; wsId: string }) =>
      api.put(
        `/workspace-legislator-tracking/workspace/${wsId}/legislator/${id}/position`,
        { position }
      ),
    {
      onSuccess: (_data, { wsId }) => {
        invalidateLegislatorTrackingCaches(wsId);
        setShowToast(t('legislatorDetail.positionUpdated'));
      },
      onError: () => {
        setShowToast(t('legislatorDetail.positionError'));
      },
    }
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchLegislator(), refetchStats()]);
    e.detail.complete();
  };

  const workspaceCanManage = (wsId: string): boolean => {
    const fromEligible = eligible.find((w) => w.id === wsId);
    const access =
      fromEligible?.access_level ??
      (selectedWorkspace?.id === wsId ? selectedWorkspace.access_level : undefined);
    return trackingCapabilitiesFromAccessLevel(access).canManage;
  };

  const applyPositionInWorkspace = (wsId: string, position: string | null) => {
    if (!workspaceCanManage(wsId)) {
      setShowToast(t('legislatorDetail.positionRequiresManage'));
      return;
    }
    if (!id) return;
    if (!isTracked) {
      api
        .post('/workspace-legislator-tracking', buildLegislatorTrackBody(id, wsId))
        .then(() => {
          invalidateLegislatorTrackingCaches(wsId);
          positionMutation.mutate({ position, wsId });
        })
        .catch(() => setShowToast(t('legislatorDetail.trackLegislatorError')));
    } else {
      positionMutation.mutate({ position, wsId });
    }
  };

  const executePendingAction = (wsId: string, pending: PendingTrackingAction) => {
    if (pending.kind === 'track' || pending.kind === 'untrack') {
      trackMutation.mutate({ action: pending.kind, wsId });
      return;
    }
    if (pending.kind === 'priority') {
      priorityMutation.mutate({ newIsPriority: pending.payload, wsId });
      return;
    }
    if (pending.kind === 'open-position') {
      if (!workspaceCanManage(wsId)) {
        setShowToast(t('legislatorDetail.positionRequiresManage'));
        return;
      }
      setPositionSelectorOpen(true);
      return;
    }
    if (pending.kind === 'position') {
      applyPositionInWorkspace(wsId, pending.payload);
    }
  };

  const openPickerOr = (pending: PendingTrackingAction, run: () => void) => {
    // Prompt during Track flow when destination is unset OR when multiple
    // workspaces exist and the user is starting a track (not untrack) — so
    // destination is confirmed in-flow, not only via the separate dropdown.
    const shouldPromptInFlow =
      status === 'needs-choice' ||
      (pending.kind === 'track' && eligible.length > 1 && !contextId);

    if (shouldPromptInFlow) {
      setPendingAction(pending);
      setPickerOpen(true);
      return;
    }
    if (status === 'none') {
      setShowToast(t('trackingWorkspace.noneHint'));
      return;
    }
    if (status !== 'selected' || !selectedId) return;
    run();
  };

  const handleTrackToggle = () => {
    const action = isTracked ? 'untrack' : 'track';
    openPickerOr({ kind: action }, () => {
      if (!selectedId) return;
      trackMutation.mutate({ action, wsId: selectedId });
    });
  };

  const handlePriorityToggle = () => {
    const next = !isPriority;
    openPickerOr({ kind: 'priority', payload: next }, () => {
      if (!selectedId) return;
      priorityMutation.mutate({ newIsPriority: next, wsId: selectedId });
    });
  };

  const handlePositionSelect = (position: string | null) => {
    setPositionSelectorOpen(false);
    openPickerOr({ kind: 'position', payload: position }, () => {
      if (!selectedId) return;
      applyPositionInWorkspace(selectedId, position);
    });
  };

  const handlePickerSelect = (workspaceId: string) => {
    const pending = pendingAction;
    setPendingAction(null);
    select(workspaceId);
    setPickerOpen(false);
    if (pending) {
      executePendingAction(workspaceId, pending);
    } else {
      // Invalidate after select so tracking status reloads for the new destination.
      queryClient.invalidateQueries(['pro-legislator-tracking', id]);
    }
  };

  const handlePickerDismiss = () => {
    // Dismiss = no write; drop any pending intent.
    setPendingAction(null);
    setPickerOpen(false);
  };

  const handleOpenPositionSelector = () => {
    if (trackingControlsDisabled) return;
    if (status === 'selected') {
      if (!canManage) {
        setShowToast(t('legislatorDetail.positionRequiresManage'));
        return;
      }
      setPositionSelectorOpen(true);
      return;
    }
    if (status === 'needs-choice') {
      setPendingAction({ kind: 'open-position' });
      setPickerOpen(true);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const fullName = legislator
    ? (legislator.full_name || `${legislator.first_name} ${legislator.last_name}`.trim())
    : '';
  const title = legislator ? getTitle(legislator) : '';
  const initials = legislator ? getInitials(legislator) : '';
  const partyBgColor = legislator ? getPartyBgColor(legislator.party_affiliation) : 'bg-party-unknown dark:bg-party-unknown-bg-dark';

  // Contact info from organized person registry data
  const email = legislator?.organized_contacts?.emails?.[0]?.value || legislator?.email || null;
  const phone = legislator?.organized_contacts?.phones?.[0]?.value || legislator?.work_phone || null;
  const website = legislator?.contact_info?.website_url || legislator?.organized_contacts?.websites?.[0]?.value || null;
  const capitolAddress = legislator?.organized_addresses?.capitol?.[0];
  const addressLine = capitolAddress
    ? [capitolAddress.address_line_1, capitolAddress.city, capitolAddress.state, capitolAddress.zip_code].filter(Boolean).join(', ')
    : null;

  // Committee memberships (structured array, NOT the comma-separated string)
  const committees = legislator?.committee_memberships || [];

  const sponsoredCount = stats?.sponsored_bills_count ?? stats?.sponsoredBillsCount ?? null;
  const votingParticipation = stats?.voting_participation != null
    ? Math.round(Number(stats.voting_participation))
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      {isLoading ? (
        <div className="px-4 py-4 space-y-4 max-w-4xl mx-auto">
          {/* Header skeleton */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full flex-shrink-0 overflow-hidden">
              <IonSkeletonText animated style={{ width: '100%', height: '100%' }} />
            </div>
            <div className="flex-1 space-y-2">
              <IonSkeletonText animated style={{ width: '60%', height: '24px' }} />
              <IonSkeletonText animated style={{ width: '40%', height: '16px' }} />
              <IonSkeletonText animated style={{ width: '50%', height: '16px' }} />
            </div>
          </div>
          <div className="rounded-xl bg-white dark:bg-background-dark-secondary/50 border border-white/10 p-4 space-y-2">
            <IonSkeletonText animated style={{ width: '30%', height: '16px' }} />
            <IonSkeletonText animated style={{ width: '80%', height: '14px' }} />
            <IonSkeletonText animated style={{ width: '70%', height: '14px' }} />
          </div>
          <div className="rounded-xl bg-white dark:bg-background-dark-secondary/50 border border-white/10 p-4 space-y-2">
            <IonSkeletonText animated style={{ width: '25%', height: '16px' }} />
            <IonSkeletonText animated style={{ width: '45%', height: '14px' }} />
            <IonSkeletonText animated style={{ width: '45%', height: '14px' }} />
          </div>
        </div>
      ) : legislator ? (
        <div className="px-4 py-4 max-w-4xl mx-auto pb-24">

          {/* ── Profile Header ───────────────────────────────────── */}
          <div className="mb-5">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div
                className={`flex-shrink-0 flex items-center justify-center ${partyBgColor} rounded-full overflow-hidden`}
                style={{ width: 80, height: 80 }}
              >
                <span className="text-white text-2xl font-bold">{initials}</span>
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-text-primary dark:text-text-dark-primary leading-tight mb-1">
                  {title ? `${title} ${fullName}` : fullName}
                </h1>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {legislator.party_affiliation && (
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPartyBadgeColor(legislator.party_affiliation)}`}
                    >
                      {formatPartyDisplay(legislator.party_affiliation)}
                    </span>
                  )}
                  {legislator.chamber && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-neutral-200 text-neutral-800 dark:bg-background-dark-tertiary/60 dark:text-text-dark-primary">
                      {legislator.chamber}
                    </span>
                  )}
                  {legislator.district && (
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      {formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)}
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2">
                  <ProTrackingDestinationLabel
                    status={status}
                    workspace={selectedWorkspace}
                    eligibleCount={eligible.length}
                    onPress={
                      contextId
                        ? undefined
                        : () => {
                            setPendingAction(null);
                            setPickerOpen(true);
                          }
                    }
                    interactive={!contextId}
                    disabled={trackingControlsDisabled}
                  />
                  <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleTrackToggle}
                      disabled={writeControlsDisabled || trackMutation.isLoading}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        isTracked
                          ? 'bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800'
                          : 'bg-background-secondary text-neutral-600 border-border-light dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary dark:border-border-dark-hairline'
                      } ${writeControlsDisabled || trackMutation.isLoading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
                    >
                      <IonIcon icon={isTracked ? bookmark : bookmarkOutline} style={{ fontSize: 14 }} />
                      {isTracked ? t('common.tracked') : t('common.track')}
                    </button>

                    {isTracked && (
                      <button
                        onClick={handlePriorityToggle}
                        disabled={writeControlsDisabled || priorityMutation.isLoading}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          isPriority
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800'
                            : 'bg-background-secondary text-neutral-600 border-border-light dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary dark:border-border-dark-hairline'
                        } ${writeControlsDisabled || priorityMutation.isLoading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
                      >
                        <IonIcon icon={isPriority ? star : starOutline} style={{ fontSize: 14 }} />
                        {isPriority ? t('legislatorDetail.priority') : t('legislatorDetail.setPriority')}
                      </button>
                    )}

                    {positionControlsDisabled && status === 'selected' ? (
                      currentPosition ? (
                        <span
                          className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPositionColorClasses(currentPosition)}`}
                          data-testid="position-readonly"
                        >
                          {currentPosition}
                        </span>
                      ) : (
                        <span
                          className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-tertiary text-neutral-500 dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary border border-neutral-400/60 dark:border-neutral-500/60 opacity-50"
                          data-testid="position-unavailable"
                        >
                          {t('common.setPosition')}
                        </span>
                      )
                    ) : (
                      <button
                        onClick={handleOpenPositionSelector}
                        disabled={positionControlsDisabled}
                        className={`inline-flex items-center ${positionControlsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="position-open"
                      >
                        {currentPosition ? (
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPositionColorClasses(currentPosition)}`}>
                            {currentPosition}
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-tertiary text-neutral-500 dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary border border-neutral-400/60 dark:border-neutral-500/60">
                            {t('common.setPosition')}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                  {positionUnavailableExplanation && (
                    <p
                      className="text-[11px] leading-snug text-text-tertiary dark:text-text-dark-tertiary"
                      data-testid="position-permission-hint"
                    >
                      {positionUnavailableExplanation}
                    </p>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Contact Info Card ────────────────────────────────── */}
          {(email || phone || addressLine || website) && (
            <IonCard className="mb-3">
              <IonCardHeader className="pb-2">
                <IonCardTitle className="text-base">{t('legislatorDetail.contactInfo')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="pt-0">
                <IonList lines="inset">
                  {email && (
                    <IonItem href={`mailto:${email}`} target="_self" detail={false}>
                      <IonLabel>
                        <p className="text-sm text-text-primary dark:text-text-dark-primary truncate">
                          {email}
                        </p>
                      </IonLabel>
                    </IonItem>
                  )}
                  {phone && (
                    <IonItem href={`tel:${phone}`} target="_self" detail={false}>
                      <IonLabel>
                        <p className="text-sm text-text-primary dark:text-text-dark-primary">
                          {phone}
                        </p>
                      </IonLabel>
                    </IonItem>
                  )}
                  {addressLine && (
                    <IonItem detail={false}>
                      <IonLabel>
                        <p className="text-sm text-text-primary dark:text-text-dark-primary whitespace-pre-line">
                          {addressLine}
                        </p>
                      </IonLabel>
                    </IonItem>
                  )}
                  {website && (
                    <IonItem
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      detail={false}
                    >
                      <IonLabel>
                        <p className="text-sm text-text-primary dark:text-text-dark-primary truncate">
                          {website.replace(/^https?:\/\//, '')}
                        </p>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              </IonCardContent>
            </IonCard>
          )}

          {/* ── Quick Stats Card ─────────────────────────────────── */}
          {stats && (
            <IonCard className="mb-3">
              <IonCardHeader className="pb-2">
                <IonCardTitle className="text-base">{t('legislatorDetail.quickStats')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="pt-0">
                <div className="grid grid-cols-3 gap-3">
                  {/* Sponsored bills */}
                  <button
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-white dark:bg-background-dark-secondary/50 border border-border-light dark:border-border-dark-hairline text-center active:scale-95 transition-transform"
                    onClick={() => navigate(`/pro/bills?sponsor=${id}`)}
                  >
                    <span className="text-2xl font-bold text-orange-400">
                      {sponsoredCount ?? '—'}
                    </span>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5 leading-tight">
                      {t('legislatorDetail.sponsoredBills')}
                    </span>
                    <IonIcon
                      icon={chevronForwardOutline}
                      className="text-text-tertiary dark:text-text-dark-tertiary mt-1"
                      style={{ fontSize: 12 }}
                    />
                  </button>

                  {/* Voting participation */}
                  <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-white dark:bg-background-dark-secondary/50 border border-border-light dark:border-border-dark-hairline text-center">
                    <span className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
                      {votingParticipation != null ? `${votingParticipation}%` : '—'}
                    </span>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5 leading-tight">
                      {t('legislatorDetail.votingRate')}
                    </span>
                  </div>

                  {/* Session year */}
                  <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-white dark:bg-background-dark-secondary/50 border border-border-light dark:border-border-dark-hairline text-center">
                    <span className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
                      {stats.session_year ?? '—'}
                    </span>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5 leading-tight">
                      {t('legislatorDetail.session')}
                    </span>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* ── Committee Memberships Card ───────────────────────── */}
          {committees.length > 0 && (
            <IonCard className="mb-3">
              <IonCardHeader className="pb-2">
                <IonCardTitle className="text-base">{t('legislatorDetail.committeeMemberships')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="pt-0">
                <IonList lines="inset">
                  {committees.map((committee, idx) => {
                    const role = committee.role || 'Member';
                    return (
                      <IonItem key={idx} detail={false}>
                        <IonLabel>
                          <p className="text-sm text-text-primary dark:text-text-dark-primary">
                            {committee.committee_name}
                          </p>
                        </IonLabel>
                        <div slot="end">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getRoleBadgeColor(role)}`}>
                            {role}
                          </span>
                        </div>
                      </IonItem>
                    );
                  })}
                </IonList>
              </IonCardContent>
            </IonCard>
          )}

          {/* ── Tracking Actions Card ────────────────────────────── */}
          <IonCard className="mb-3">
            <IonCardHeader className="pb-2">
              <IonCardTitle className="text-base">{t('legislatorDetail.tracking')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent className="pt-0">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {t('legislatorDetail.yourPosition')}
                  </span>
                  {positionControlsDisabled && status === 'selected' ? (
                    currentPosition ? (
                      <span
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPositionColorClasses(currentPosition)}`}
                        data-testid="position-readonly-card"
                      >
                        {currentPosition}
                      </span>
                    ) : (
                      <span
                        className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-tertiary text-neutral-500 dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary border border-neutral-400/60 dark:border-neutral-500/60 opacity-50"
                        data-testid="position-unavailable-card"
                      >
                        {t('common.notSet')}
                      </span>
                    )
                  ) : (
                    <button
                      onClick={handleOpenPositionSelector}
                      disabled={positionControlsDisabled}
                      className={`inline-flex items-center ${positionControlsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="position-open-card"
                    >
                      {currentPosition ? (
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPositionColorClasses(currentPosition)}`}>
                          {currentPosition}
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-tertiary text-neutral-500 dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary border border-neutral-400/60 dark:border-neutral-500/60">
                          {t('common.notSet')}
                        </span>
                      )}
                    </button>
                  )}
                </div>

                {positionControlsDisabled && status === 'selected' ? (
                  <p
                    className="text-[11px] leading-snug text-text-tertiary dark:text-text-dark-tertiary"
                    data-testid="position-permission-hint-card"
                  >
                    {positionUnavailableExplanation}
                  </p>
                ) : (
                  <IonButton
                    expand="block"
                    fill="outline"
                    size="small"
                    disabled={positionControlsDisabled}
                    onClick={handleOpenPositionSelector}
                  >
                    {t('legislatorDetail.setPosition')}
                  </IonButton>
                )}
              </div>
            </IonCardContent>
          </IonCard>

        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center px-4">
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {t('legislatorDetail.legislatorNotFound')}
          </p>
          <IonButton
            fill="clear"
            size="small"
            className="mt-3"
            onClick={() => navigate('/pro/legislators')}
          >
            {t('common.backToLegislators')}
          </IonButton>
        </div>
      )}

      {/* ── Position Selector ─────────────────────────────────── */}
      <ProPositionSheet
        isOpen={positionSelectorOpen}
        currentPosition={currentPosition}
        onSelect={handlePositionSelect}
        onDismiss={() => setPositionSelectorOpen(false)}
        type="legislator"
      />

      {/* ── Tracking workspace picker ────────────────────────── */}
      <ProTrackingWorkspacePicker
        isOpen={pickerOpen}
        eligible={eligible}
        selectedId={selectedId}
        onSelect={handlePickerSelect}
        onDismiss={handlePickerDismiss}
      />

      {/* ── Toast ─────────────────────────────────────────────── */}
      <IonToast
        isOpen={!!showToast}
        message={showToast}
        duration={2500}
        onDidDismiss={() => setShowToast('')}
        position="bottom"
      />
    </>
  );
};

export default ProLegislatorDetailPage;
